// 创建axios实例
import axios from "axios";
import QS from "qs";
import { Loading, Message, MessageBox } from "element-ui";
import { getStore, setStore } from "@/units/store";
import store from '@/store'
import { baseUrl, defaultTenantId } from '@/units/config';
axios.defaults.baseURL = baseUrl;

const service = axios.create({
  timeout: 30000, // 请求超时时间
  // 兼容后端对数组参数的解析：type[]=seckill
  paramsSerializer: (params) => QS.stringify(params, { arrayFormat: "brackets" }),
  // transformRequest: data => qs.stringify(data)
});
let loadingInstance = null;
let loadingCount = 0;

function startLoading() {
  if (loadingCount === 0) {
    loadingInstance = Loading.service({
      text: '',
      spinner: 'el-icon-loading',
      background: 'rgba(0,0,0,0)',
    });
  }
  loadingCount += 1;
}

function endLoading() {
  if (loadingCount <= 0) {
    loadingCount = 0;
    return;
  }
  loadingCount -= 1;
  if (loadingCount === 0 && loadingInstance) {
    loadingInstance.close();
    loadingInstance = null;
  }
}

let isLoginRedirecting = false;

/** 首页/列表/详情浏览用公开读接口：未登录时 401 不弹「跳转登录」 */
const PUBLIC_BROWSE_API_SNIPPETS = [
  'goods.goods/detail',
  'goods.goods/index',
  'goods.goods/ids',
  'goods.goods/activityList',
  'goods.goods/activity',
  'goods.comment/',
  'activity.activity/index',
  'activity.activity/detail',
  'mall.activity.activity/index',
  'mall.activity.activity/detail',
  'category/',
  'index/',
  'app.',
];

function isPublicBrowseApi(config) {
  const url = String((config && config.url) || '');
  return PUBLIC_BROWSE_API_SNIPPETS.some((s) => url.indexOf(s) !== -1);
}

/** 是否弹出登录框并跳转（游客浏览公开接口、或当前在首页时不强提示） */
function shouldForceLoginRedirect(config) {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const onHome = path === '/' || path === '';
  if (onHome) {
    return false;
  }
  const hadToken = !!(store.getters && store.getters.token);
  if (!hadToken && isPublicBrowseApi(config)) {
    return false;
  }
  return true;
}

function getHttpStatus(error) {
  return error && error.response && error.response.status;
}

function isTokenExpirationMsg(msg) {
  return /token\s*expir/i.test(String(msg || ''));
}

/** HTTP 401、业务码 401，或后端英文 Token expiration */
function isUnauthorized(data, httpStatus) {
  if (httpStatus === 401) return true;
  if (data && (data.code == 401 || data.status == 401)) return true;
  if (data && isTokenExpirationMsg(data.msg)) return true;
  return false;
}

function formatHttpErrorMessage(error) {
  const data = error && error.response && error.response.data;
  if (data && typeof data === 'object' && data.msg) {
    return data.msg;
  }
  const status = getHttpStatus(error);
  if (status === 404) {
    return '接口不存在(404)，请确认后端已启动且路由正确';
  }
  if (status === 403) return '没有访问权限';
  if (status === 500) return '服务器错误，请稍后重试';
  if (status === 502 || status === 503) return '服务暂时不可用，请稍后重试';
  const axMsg = (error && error.message) || '';
  if (/status code 404/i.test(axMsg)) {
    return '接口不存在(404)，请确认后端已启动且路由正确';
  }
  if (/status code/i.test(axMsg)) {
    return '请求失败，请稍后重试';
  }
  return axMsg || '网络异常，请稍后重试';
}

/** 非关键接口 404 不弹 Toast（登录后 Header 拉购物车数量等） */
function shouldSilenceHttpError(config, error) {
  if (getHttpStatus(error) !== 404) return false;
  const url = String((config && config.url) || '');
  const optional404 = [
    'cart/index',
    'mall.cart/index',
    'activity.activity/index',
    'mall.activity.activity/index',
  ];
  return optional404.some((s) => url.indexOf(s) !== -1);
}

function redirectToLogin() {
  const redirect = encodeURIComponent(window.location.pathname + window.location.search + window.location.hash);
  window.location.href = `/Login?redirect=${redirect}`;
}

function handle401(msg = '请登录后操作') {
  if (isLoginRedirecting) return;
  isLoginRedirecting = true;

  if (isTokenExpirationMsg(msg)) {
    msg = '登录已过期，请重新登录';
  }

  // 3 秒后自动跳转，点击确定立即跳转
  const timer = setTimeout(() => {
    redirectToLogin();
  }, 3000);

  MessageBox.alert(`${msg}，3秒后将自动跳转到登录页。`, '提示', {
    confirmButtonText: '立即登录',
    showClose: false,
    type: 'warning',
    callback: () => {
      clearTimeout(timer);
      redirectToLogin();
    }
  }).catch(() => {
    // ignore
  });
}

// 添加请求拦截器
service.interceptors.request.use(
  function(config) {
    startLoading();

    // 平台
    config.headers['platform'] = 'H5';
    config.headers["token"] = store.getters.token || ''
    // 租户 ID 来自 Vuex（启动时写入）；空时回退到 config.defaultTenantId
    const tid = store.getters && store.getters.tenant_id;
    config.headers['tenant-id'] = tid || defaultTenantId || '';

    axios.defaults.headers["accept"] = "*/*";
    axios.defaults.headers["contentType"] = "application/x-www-form-urlencoded";
    // 在发送请求之前做些什么
    return config;
  },
  function(error) {
    // 对请求错误做些什么
    console.log("ajaxError");
    return Promise.reject(error);
  }
);
// 添加响应拦截器
service.interceptors.response.use(
  response => {
    // 自动设置登陆令牌
    if (response.headers.token) {
      store.commit('user/SET_TOKEN', response.headers.token)
    }

    // 对响应数据做点什么
    let data = {
      code: response.data.code,
      data: response.data.data
    };

    endLoading();
    if (response.data.code != 1) {
      const is401 = isUnauthorized(response.data, response.status);
      const silent401 = is401 && !shouldForceLoginRedirect(response.config);
      // 401 / Token expiration：首页静默；其它页只弹登录框，避免再出一层英文 Toast
      if (!silent401 && !is401) {
        Message.error(response.data.msg);
      }
      if (is401) {
        store.commit('user/CLEAR_TOKEN');
        if (!silent401) {
          handle401(response.data.msg || '请登录后操作');
        }
      }
      const err = new Error(response.data && response.data.msg ? response.data.msg : '请求失败');
      err.data = response.data;
      err.code = response.data && response.data.code;
      err.url = response && response.config ? response.config.url : '';
      return Promise.reject(err);
    } else {
      // Message({
      //   message: response.data.msg,
      //   type: "success"
      // });
    }
    return data;
  },
  (error) => {
    endLoading();
    const msg = formatHttpErrorMessage(error);
    const silentHttp = shouldSilenceHttpError(error.config, error);

    const is401 = isUnauthorized(
      error && error.response && error.response.data,
      getHttpStatus(error)
    );
    const silent401 = is401 && !shouldForceLoginRedirect(error.config);
    if (!silent401 && !silentHttp && !is401) {
      Message({
        message: msg,
        type: "error"
      });
    }

    if (is401) {
      store.commit('user/CLEAR_TOKEN');
      if (!silent401) {
        handle401((error.response && error.response.data && error.response.data.msg) || '请登录后操作');
      }
    }

    // setTimeout(res => {
    //   localStorage.removeItem("userInfo");
    //   location.href = "/login";
    // }, 3000);
    const err = error instanceof Error ? error : new Error(msg);
    if (error && error.response && error.response.data) {
      err.data = error.response.data;
      err.code = error.response.data.code;
    }
    err.url = error && error.config ? error.config.url : '';
    return Promise.reject(err);
  }
);

/**
 * get方法，对应get请求
 * @param {String} url [请求的url地址]
 * @param {Object} params [请求时携带的参数]
 * @param {Object} loading [是否显示Loading与Toast,{isLoading: true,isToast: false}]
 */
export function get(
  url,
  params,
  loading = {
    isLoading: true,
    isToast: false
  }
) {
  return new Promise((resolve, reject) => {
    service
      .get(url, {
        params: params
      })
      .then(res => {
        resolve(res.data);
      })
      .catch(err => {
        reject(err);
      });
  });
}

/**
 * post方法，对应post请求
 * @param {String} url [请求的url地址]
 * @param {Object} params [请求时携带的参数]
 * @param {Object} loading [是否显示Loading与Toast,{isLoading: true,isToast: false}]
 */
export function post(
  url,
  params,
  loading = {
    isLoading: true,
    isToast: false
  }
) {
  return new Promise((resolve, reject) => {
    service
      .post(url, QS.stringify(params))
      .then(res => {
        resolve(res.data);
      })
      .catch(err => {
        reject(err);
      });
  });
}
// 最后暴露实例
export default service;
